/**
 * AI Organize (LLM endpoint) form for the Settings dialog.
 *
 * Mirrors `StorageCredentialForm`: the API key is write-only (blank keeps the
 * stored one), Test proves the endpoint with one tiny completion, and Save
 * applies live — the next organize run uses it immediately.
 */
import { useState, type FormEvent } from 'react';
import { Loader2 } from 'lucide-react';
import { ApiError } from '../lib/api/client.js';
import { llmApi, type LLMSettingsInput, type LLMSettingsView } from '../lib/api/llm.js';

type Status = { kind: 'idle' } | { kind: 'ok' | 'error'; message: string };

interface LlmSettingsFormProps {
  initial: LLMSettingsView | null;
  /** Reports test/save busy state so the modal can block Escape/backdrop close. */
  onBusyChange?: (busy: boolean) => void;
  onSaved: (llm: LLMSettingsView) => void;
}

export function LlmSettingsForm({ initial, onBusyChange, onSaved }: LlmSettingsFormProps) {
  const [baseURL, setBaseURL] = useState(initial?.baseURL ?? '');
  const [apiKey, setApiKey] = useState('');
  const [model, setModel] = useState(initial?.model ?? '');
  const [status, setStatus] = useState<Status>({ kind: 'idle' });
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);

  const busy = testing || saving;

  function currentInput(): LLMSettingsInput {
    return {
      // Blank key means "unchanged" — the server keeps the stored one.
      baseURL: baseURL.trim(),
      apiKey: apiKey.trim(),
      model: model.trim(),
    };
  }

  async function onTest(): Promise<void> {
    if (!initial?.configured) return; // Test always proves the stored endpoint.
    setTesting(true);
    onBusyChange?.(true);
    setStatus({ kind: 'idle' });
    try {
      const result = await llmApi.test();
      setStatus({ kind: result.ok ? 'ok' : 'error', message: result.message });
    } catch (caught) {
      setStatus({
        kind: 'error',
        message: caught instanceof ApiError ? caught.message : 'Could not reach the server.',
      });
    } finally {
      setTesting(false);
      onBusyChange?.(false);
    }
  }

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (busy) return;
    setSaving(true);
    onBusyChange?.(true);
    setStatus({ kind: 'idle' });
    try {
      const result = await llmApi.saveSettings(currentInput());
      setApiKey('');
      setStatus({ kind: 'ok', message: 'Saved. AI Organize uses this endpoint immediately.' });
      onSaved(result.llm);
    } catch (caught) {
      setStatus({
        kind: 'error',
        message: caught instanceof ApiError ? caught.message : 'Could not save the settings.',
      });
    } finally {
      setSaving(false);
      onBusyChange?.(false);
    }
  }

  return (
    <form onSubmit={onSubmit} className="storage-form">
      {!initial?.persist && (
        <div className="settings-note">
          The server keeps settings in memory only, so this configuration is lost on restart.
        </div>
      )}

      <label className="settings-field">
        <span className="settings-label">Base URL</span>
        <input
          className="input"
          value={baseURL}
          placeholder="https://api.deepseek.com/v1"
          autoComplete="off"
          spellCheck={false}
          disabled={busy}
          onChange={(event) => setBaseURL(event.target.value)}
        />
      </label>

      <label className="settings-field">
        <span className="settings-label">API key{initial?.hasApiKey ? ' (stored)' : ''}</span>
        <input
          className="input"
          type="password"
          value={apiKey}
          autoComplete="new-password"
          spellCheck={false}
          placeholder={initial?.hasApiKey ? 'Leave blank to keep the stored key' : 'sk-…'}
          disabled={busy}
          onChange={(event) => setApiKey(event.target.value)}
        />
      </label>

      <label className="settings-field">
        <span className="settings-label">Model</span>
        <input
          className="input"
          value={model}
          placeholder="deepseek-chat"
          autoComplete="off"
          spellCheck={false}
          disabled={busy}
          onChange={(event) => setModel(event.target.value)}
        />
      </label>

      {status.kind !== 'idle' && (
        <div
          className={
            status.kind === 'ok'
              ? 'settings-status settings-status-ok'
              : 'settings-status settings-status-error'
          }
          role="status"
        >
          {status.message}
        </div>
      )}

      <div className="modal-actions">
        <button
          type="button"
          className="button button-quiet"
          onClick={onTest}
          disabled={busy || !initial?.configured}
          title={initial?.configured ? undefined : 'Save an endpoint first'}
        >
          {testing ? (
            <>
              <Loader2 size={13} strokeWidth={2} className="spin" /> Testing…
            </>
          ) : (
            'Test connection'
          )}
        </button>
        <button type="submit" className="button" disabled={busy}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  );
}
